// Ayarlar ve Tanımlamalar ortak kabuğu: Estesoft web `clientapp/src/services/settingsNavigation.js`
// sözleşmesinin Vega uyarlaması (bkz. clientapp docs/styleguide/gezinme.md → "Ayarlar ve
// Tanımlamalar ortak kabuğu"). İki bölüm aynı bileşeni, aynı grup adlarını ve aynı sırayı
// kullanır; kullanıcı gezinmeyi bir kez öğrenir. Bu dosya saf yardımcıdır: Vue, router veya
// store import etmez; test Node'da doğrudan yükler.
const group = name => `SettingsNavigation.${name}`;

// Grup adları ve sırası Estesoft web ile birebir aynıdır. Vega'da karşılığı olmayan grup
// (CRM, Mesaj...) basılmaz: groupMenuItems yalnız öğesi olan grupları döndürür.
export const SHARED_GROUP_ORDER = [
  group('Clinic'),
  group('Customer'),
  group('CrmLead'),
  group('Appointment'),
  group('Treatment'),
  group('FormsConsent'),
  group('Messaging'),
  group('SalesProposal'),
  group('CollectionBank'),
  group('Expense'),
  group('HealthTourism'),
  group('Complaint')
];

// Grup adı konu söyler, kapsam söylemez; eşleme sırası grup içi satır sırasıdır.
export const SETTINGS_NAVIGATION_SECTIONS = [
  {
    key: 'settings',
    label: 'Settings',
    parentRouteName: 'settings',
    dataUrl: '/api/VegaCommon/GetManagementMenu',
    permission: 'other_definitions',
    landing: {
      icon: 'fa-solid fa-sliders',
      description: 'SettingsNavigation.Vega.Overview.SettingsDescription',
      guide: 'SettingsNavigation.Overview.SettingsGuide'
    },
    groupOrder: SHARED_GROUP_ORDER,
    routeGroups: {
      'settings.general': group('Clinic'),
      'settings.customer': group('Customer'),
      // Doktor Takvimi Entegrasyonu çevrim içi randevu kanalıdır.
      'settings.docplanner': group('Appointment'),
      'settings.form': group('FormsConsent')
    },
    activeRouteAliases: {}
  },
  {
    key: 'definitions',
    label: 'Definitions',
    parentRouteName: 'definitions',
    dataUrl: '/api/VegaCommon/GetDefinitionsMenu',
    permission: 'other_definitions',
    landing: {
      icon: 'fa-solid fa-shapes',
      description: 'SettingsNavigation.Vega.Overview.DefinitionsDescription',
      guide: 'SettingsNavigation.Overview.DefinitionsGuide'
    },
    groupOrder: SHARED_GROUP_ORDER,
    routeGroups: {
      'branchRoom.list': group('Clinic'),
      'formTemplate.list': group('FormsConsent'),
      'consentForm.list': group('FormsConsent'),
      'customConsentForm.list': group('FormsConsent'),
      'bankInfo.list': group('CollectionBank'),
      'expenseType.list': group('Expense')
    },
    activeRouteAliases: {}
  }
];

// Eşlemede olmayan yeni sunucu öğesi bu gruba düşer; Vega menü uçları grupsuz düz liste döner.
export const FALLBACK_GROUP = group('Vega.Other');

export const SETTINGS_NAVIGATION_SHELL_KEY = 'settings-navigation';

export function sectionByKey(key) {
  return SETTINGS_NAVIGATION_SECTIONS.find(section => section.key === key) || null;
}

function matchedNames(route) {
  return ((route && route.matched) || []).map(record => record.name);
}

// Layout'un router-view anahtarı. İki bölüm tek kabuk örneğini paylaşır: sekme veya ekran
// değişince kabuk yeniden kurulmaz, menü yanıtları ve açık gruplar korunur.
export function appRouteViewKey(route, fallbackKey) {
  const shellName = matchedNames(route)[0];
  return SETTINGS_NAVIGATION_SECTIONS.some(section => section.parentRouteName === shellName)
    ? SETTINGS_NAVIGATION_SHELL_KEY
    : fallbackKey;
}

export function sectionForRoute(route) {
  const names = matchedNames(route);
  return SETTINGS_NAVIGATION_SECTIONS.find(section => names.includes(section.parentRouteName)) || null;
}

// Vega'da ana menü store'da tutulmaz; bölüm görünürlüğü izin ile belirlenir. Menü ucu
// yetkiye göre satır süzer, boş yanıt boş durumu gösterir.
export function canAccessSection(section, checkPermission) {
  return !!section
    && typeof checkPermission === 'function'
    && checkPermission(section.permission) === true;
}

// Vega menü uçları düz liste döner; Estesoft web gibi grup sözlüğü gelirse de kabul edilir.
function sourceEntries(menuItems) {
  if (Array.isArray(menuItems)) return [[FALLBACK_GROUP, menuItems]];
  if (menuItems && typeof menuItems === 'object') return Object.entries(menuItems);
  return [];
}

export function groupMenuItems(section, menuItems) {
  const grouped = new Map();
  const mappedRoutes = Object.keys(section.routeGroups);
  // Grup içi satır sırası eşleme sırasıdır; eşlemede olmayan öğe sunucu sırasını koruyarak
  // grubun sonuna düşer.
  const rank = item => {
    const index = mappedRoutes.indexOf(item && item.routeName);
    return index === -1 ? mappedRoutes.length : index;
  };

  sourceEntries(menuItems).forEach(([sourceGroup, items]) => {
    if (!Array.isArray(items)) return;

    items.forEach(item => {
      if (!item) return;
      const groupName = section.routeGroups[item.routeName] || sourceGroup;
      if (!grouped.has(groupName)) grouped.set(groupName, []);
      grouped.get(groupName).push(item);
    });
  });

  grouped.forEach(items => {
    // Array.prototype.sort kararlıdır: eşit sıradaki öğeler sunucu sırasını korur.
    items.sort((left, right) => rank(left) - rank(right));
  });

  const configured = section.groupOrder
    .filter(groupName => grouped.has(groupName))
    .map(groupName => ({ name: groupName, items: grouped.get(groupName) }));
  const configuredNames = new Set(section.groupOrder);
  const fallbacks = [...grouped.entries()]
    .filter(([groupName]) => !configuredNames.has(groupName))
    .map(([name, items]) => ({ name, items }));

  return [...configured, ...fallbacks];
}

// Alt route ve onun modalı (ör. Yeni Ödeme Aracı) açıkken ana liste satırı etkin kalır.
export function itemForMatchedRoute(section, groups, route) {
  const names = new Set(matchedNames(route));
  const aliasedNames = new Set(
    [...names].map(name => section.activeRouteAliases[name]).filter(Boolean)
  );

  for (const menuGroup of groups || []) {
    const item = menuGroup.items.find(candidate =>
      names.has(candidate.routeName) || aliasedNames.has(candidate.routeName)
    );
    if (item) return { group: menuGroup, item };
  }

  return null;
}

// Dokunulmamış grup açıktır; yalnız kullanıcının kapattığı grup false tutulur
// (açma/kapama anahtara doğrudan atamayla yazılır, Vue 3 yeni anahtarı izler).
export function isGroupOpenState(groups, groupName) {
  return !groups || groups[groupName] !== false;
}

export function isSectionRootRoute(section, route) {
  return !!section && !!route && route.name === section.parentRouteName;
}

export function isCurrentRequest(requestSequence, currentSequence, requestBranchId, currentBranchId) {
  return requestSequence === currentSequence && requestBranchId == currentBranchId;
}
